/*
 * @Author: LZT
 * @Date: 2024-12-09 16:28:08
 * @LastEditors: ITDLCL
 * @Description:通用函数
 * @FilePath: \qs\src\utils\index.js
 */
import router from "@/router/index";
import VConsole from "vconsole";
import { formatChinaTime } from "@/utils/time";

export function getAssetsImages (name) {
  const url = `/src/assets/images/${name}`; // 简化路径处理
  return new URL(url, import.meta.url).href;
}

/**
 * 判断是否是微信浏览器
 */
export function isWeixin (type = 1) {
  const ua = navigator.userAgent.toLowerCase();
  // 企业微信浏览器
  const isWeChat = /micromessenger/i.test(ua) && !/wxwork/i.test(ua);
  const appName = router.currentRoute.value.query.appName;
  if (type == 1) {
    return isWeChat || getUnionid() || appName;
  } else {
    return isWeChat || appName;
  }
}

/**
 * 判断运行环境 - 区分微信内置浏览器、手机浏览器、App环境
 * @returns {Object} 返回环境信息对象
 * platform: 'weChat'微信H5 | 'mobile_browser'企维H5 | 'ios_app'苹果app | 'android_app'安卓app | 'desktop'桌面 | 'unknown'未知,
 * os: 'ios'苹果手机 | 'android'安卓手机 | 'desktop'桌面 | 'unknown'未知,
 * isWeChat: boolean是否是微信环境
 * isWeChat: boolean是否是企业微信环境
 * isMobile: boolean是否是移动设备,
 * isApp: boolean是否是App环境,
 * isDesktop: boolean是否是桌面环境
 */
export function getEnvironment() {
  const ua = navigator.userAgent.toLowerCase();
  const result = {
    platform: 'unknown',
    os: 'unknown',
    isWeChat: false,
    isWeChatWork: false,
    isMobile: false,
    isApp: false,
    isDesktop: false
  };

  // 判断操作系统
  if (/iphone|ipad|ipod|ios/.test(ua)) {
    result.os = 'ios';
  } else if (/android/.test(ua)) {
    result.os = 'android';
  } else if (/windows|macintosh|linux/.test(ua)) {
    result.os = 'desktop';
  }

  // 判断是否为移动设备
  result.isMobile = /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(ua);
  // 判断是否为桌面设备
  result.isDesktop = !result.isMobile;

  // 判断是否为微信环境
  const isWeChat = /micromessenger/.test(ua) && !/wxwork/.test(ua);
  const isWeChatWork = /wxwork/.test(ua);
  result.isWeChat = isWeChat; // 微信
  result.isWeChatWork = isWeChatWork; // 企业微信

  // 通过URL参数判断App环境
  const urlParams = new URLSearchParams(window.location.search);
  const isAppFromUrl = urlParams.get('app') === 'true'; // 检测App标识符
  result.isApp = isAppFromUrl;

  // 确定平台类型
  if (isWeChat) {
    result.platform = 'weChat';
  } else if (isWeChatWork) {
    result.platform = 'weChat_work';
  } else if (isAppFromUrl && result.os === 'ios') {
    result.platform = 'ios_app';
  } else if (isAppFromUrl && result.os === 'android') {
    result.platform = 'android_app';
  } else if (result.isMobile) {
    result.platform = 'mobile_browser';
  } else if (result.isDesktop) {
    result.platform = 'desktop';
  }

  return result;
}

/**
 * 时间转换显示固定格式
 * @param time 时间(Date或字符串时间或数字时间戳)
 * @param cFormat 时间显示格式
 * @returns
 */
export function parseTime (time, cFormat = "") {
  if (!time) {
    return null;
  }
  // 时间显示格式
  const format = cFormat || "{y}-{m}-{d} {h}:{i}:{s}";
  let date;
  if (typeof time === "object") {
    date = time; // Date对象
  } else {
    // 字符串时间
    if (typeof time === "string") {
      // 字符串时间戳
      if (/^[0-9]+$/.test(time)) {
        time = parseInt(time);
      } else {
        // 兼容苹果浏览器-字符串时间(年-月-日）格式转换成(年/月/日）
        time = time.replace(new RegExp(/-/gm), "/");
      }
    }
    // 数字时间
    if (typeof time === "number" && time.toString().length === 10) {
      time = time * 1000;
    }
    date = new Date(formatChinaTime(time));
  }
  const formatObj = {
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
    h: date.getHours(),
    i: date.getMinutes(),
    s: date.getSeconds(),
    a: date.getDay()
  };
  const time_str = format.replace(/{([ymdhisa])+}/g, (result, key) => {
    const value = formatObj[key];
    // 获取周期
    if (key === "a") {
      return ["日", "一", "二", "三", "四", "五", "六"][value];
    }
    return value.toString().padStart(2, "0");
  });
  return time_str;
}

/**
 * 防抖
 * @param {Function} func 待执行的方法
 * @param {number} wait 等等时间
 * @param {boolean} immediate 是否立即执行
 * @return {*}
 */
export function debounce (func, wait, immediate) {
  let timeout, args, context, timestamp, result;
  const later = () => {
    // 据上一次触发时间间隔
    const last = +new Date() - timestamp;

    // 上次被包装函数被调用时间间隔 last 小于设定时间间隔 wait
    if (last < wait && last > 0) {
      timeout = setTimeout(later, wait - last);
    } else {
      timeout = null;
      // 如果设定为immediate===true，因为开始边界已经调用过了此处无需调用
      if (!immediate) {
        result = func.apply(context, args);
        if (!timeout) context = args = null;
      }
    }
  };

  return function (...args) {
    context = this;
    timestamp = +new Date();
    const callNow = immediate && !timeout;
    // 如果延时不存在，重新设定延时
    if (!timeout) timeout = setTimeout(later, wait);
    if (callNow) {
      result = func.apply(context, args);
      context = args = null;
    }
    return result;
  };
}

/**
 * 保留俩位小数 四舍五入
 * @param {*} val
 * @returns
 */
export function handlePrice (val) {
  if (val != null && val != undefined && val !== "") {
    const num = parseFloat(val);
    if (!isNaN(num)) {
      return num.toFixed(2);
    } else {
      return "0.00";
    }
  }
  return "--";
}
/**
 * 涨跌幅 显示%
 * @param {*} val
 */
export function handleChg (val, showPercent = true, length = 2) {
  if (typeof val === "string") {
    val = Number(val);
  }
  if (val > 0) {
    return `${showPercent ? "+" : ""}${val.toFixed(length)}%`;
  } else if (val < 0) {
    return `${val.toFixed(length)}%`;
  } else {
    if (isNaN(val)) {
      return '--';
    } else {
      return length == 2 ? "0.00%" : "0%";
    }
  }
}

/**
 * 涨跌幅 显示颜色
 * @param {*} val
 */
export function handleChgColor (val) {
  if (val > 0) {
    return "rise-text";
  } else if (val < 0) {
    return "fall-up-text";
  } else {
    return "black-text";
  }
}

/**
 * 跳转
 * @param {*} path
 * @param {*} params query
 * @param {*} isLogin 是否需要登录
 * @returns
 */
export function openPage (path, params = {}, isLogin = false) {
  // debugger;
  if (!path) return;
  router.push({
    path: path,
    query: { ...params, timeKey: new Date().getTime() }
  });
}
/**
 * 跳转
 * @param {*} path
 * @param {*} params
 */
export function pushPage ({ path, query = {}}) {
  openPage(path, query);
}
/**
 * 替换当前路由
 * @param {*} path
 * @param {*} params
 */
export function replacePage ({ path, query = {}}) {
  router.replace({
    path,
    query: { ...query, timeKey: new Date().getTime() }
  });
}

/**
 * 获取股票的code
 * @param {*} code
 */
export function getCode (code) {
  code =
    code && code.split(".")[0] && typeof code.split(".")[0] == "string"
      ? code.split(".")[0]
      : "";
  if (!code) return;
  const prefix = code.split(".")[0].slice(0, 2);
  const scode = ["00", "30", "39"].includes(prefix)
    ? code + ".XSHE"
    : ["60", "68"].includes(prefix)
      ? code + ".XSHG"
      : code;
  return scode;
}


/*
 * 设置setLocalStorage
 * */
export function setLocalStorage (key, value) {
  window.localStorage.setItem(key, value);
}
/*
 * 获取getLocalStorage
 * */
export function getLocalStorage (key) {
  return window.localStorage.getItem(key)
    ? window.localStorage.getItem(key)
    : "";
}



/**
 * 获取uinionid
 * 兼容H5页面存储JSON的问题
 */
export function getUnionid () {
  let unionid;
  try {
    unionid = JSON.parse(getLocalStorage("uc_weixin_unionId_qs")); // 部分字段存的是JSON格式的
  } catch {
    unionid = getLocalStorage("uc_weixin_unionId_qs") || "";
  }
  return unionid;
}



// 控制台
export function isTestAccount () {
  // 生产不需要 打开控制台
  const isPROD = import.meta.env.PROD;
  // 使用 getUnionid() 函数获取 unionId，兼容 JSON 格式存储
  const uc_weixin_unionId_qs = getUnionid();
  if (isPROD && uc_weixin_unionId_qs) {
    const uuid = [
      'oGCPm6TOorZblbibIIo4NTw4VykI',
      'oGCPm6cyc95He__Bnc6Ha_Efd4r4',
      'oGCPm6eq_7Er077k_RzCtn3WSZcw',
      'oGCPm6WOgHKCW1waLCbAY2XTsGpo',
      'oGCPm6ffHWd1r5aa6dADMVktI1EU',
      'oGCPm6eaY_qXRj1rPQy6mg_M6DCc',
      'oGCPm6WZcW40VCgO6IqKE4_ghgCc',
      'oGCPm6c3o87J_g6RKIJzIi0uBXL4',
      'oGCPm6T3q3h57U9_YG6b8FG_fN1k',
      'oGCPm6YqbHjuf0neunfBL11K9hoI'
    ];
    if (uuid.includes(uc_weixin_unionId_qs)) {
      new VConsole();
    }
  }
  if (!isPROD) {
    new VConsole();
  }
}


/**
 * 判断是否抖音
 * @returns
 */
export function isInDouyin () {
  return router.currentRoute.value.query.appName == "douyin";
}
/**
 * 隐藏股票名称
 * @param {*} name 股票名称
 * @param {*} auth 权限
 * @returns
 */
export function hideStockName (name, auth) {
  if (isInDouyin()) {
    return new Array(name.length).fill("*").join("");
  } else {
    return auth ? name : "****";
  }
}
/**
 * 隐藏股票代码
 * @param {*} code 股票代码
 * @param {*} auth 权限
 * @returns
 */
export function hideStockCode (code, auth) {
  if (isInDouyin()) {
    return new Array(6).fill("*").join("");
  } else {
    return auth ? code : "******";
  }
}
